import { Avatar, Box, ClickAwayListener, IconButton, Tooltip, Typography, useTheme } from '@mui/material';
import Input from 'components/Common/Input';
import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { useGetAgent } from 'apis/business/agent/hook';
import { MdMic, MdOutlineNotifications } from 'react-icons/md';
import { HiChatBubbleBottomCenterText } from 'react-icons/hi2';
import AudioWave from 'assets/audioWave.gif';
import PersonalizedLogo from 'assets/icons/PersonalizedLogo';
import PersonalizedImage from 'assets/PersonalizedImage.webp';
import Grid from '@mui/material/Grid2';
import CopyIcon from 'assets/icons/CopyIcon';
import { useGetOrganizations } from 'apis/agency/organizations/hook';

export default function PersonalizedLink() {
  const { control } = useForm();
  const theme = useTheme();
  const [open, setOpen] = useState(false);
  const { data: organizations } = useGetOrganizations();

  const handleTooltipClose = () => {
    setOpen(false);
  };

  const handleTooltipOpen = () => {
    setOpen(true);
    handleCopy();
  };

  const { data: agent } = useGetAgent();

  const handleCopy = async () => {
    const link = agent?.personalized_link ?? '';
    await navigator.clipboard.writeText(link);
  };

  return (
    <Box>
      <Typography variant='h6' fontWeight='500' sx={{ fontSize: { xs: '1.125rem', lg: '1.25rem' } }}>
        Personalized Link
      </Typography>
      <Typography color='secondary' mt={0.5} sx={{ fontSize: { xs: 14, lg: 16 } }}>
        Personalized link can be shared through any medium, such as WhatsApp, Email, SMS, etc.
      </Typography>
      <Box position='relative' marginTop={2}>
        <Input
          name='Link'
          label='Link'
          control={control}
          value={agent?.personalized_link ?? ''}
          sx={{ background: 'white' }}
          fullWidth
          disabled
        />
        <ClickAwayListener onClickAway={handleTooltipClose}>
          <Tooltip
            PopperProps={{
              disablePortal: true
            }}
            onClose={handleTooltipClose}
            open={open}
            disableFocusListener
            disableHoverListener
            disableTouchListener
            placement='top'
            title='Copied'
          >
            <IconButton
              onClick={handleTooltipOpen}
              sx={{
                position: 'absolute',
                top: '10px',
                right: 2,
                p: { xs: 0.75, lg: 1 }
              }}
            >
              <CopyIcon />
            </IconButton>
          </Tooltip>
        </ClickAwayListener>
      </Box>

      <Grid container spacing={3} mt={4}>
        <Grid size={{ xs: 12, md: 4 }}>
          <Box
            bgcolor='#fff'
            padding={{ xs: 2, lg: 2 }}
            height='100%'
            mt={1}
            sx={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
              borderRadius: '8px'
            }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', width: '100%' }}>
              <PersonalizedLogo />
              <Typography variant='h6' fontSize='10px' ml={1} sx={{ fontSize: { xs: 10, lg: 10 } }}>
                Smile Mate Dental Clinic
              </Typography>
              <Box sx={{ marginLeft: 'auto' }}>
                <IconButton
                  sx={{
                    p: { xs: 0.5, lg: 1 }
                  }}
                >
                  <MdOutlineNotifications fontSize={14} />
                </IconButton>
              </Box>
            </Box>

            <Box
              sx={{
                width: '100%',
                maxWidth: '228px',
                textAlign: 'center',
                marginTop: '2rem'
              }}
            >
              <Box position='relative'>
                <Typography variant='body2' fontWeight={500} sx={{ marginTop: '1rem' }} marginBottom={0.5}>
                  John
                </Typography>
                <Typography variant='body2' fontSize='10px' fontWeight={500}>
                  01:23
                </Typography>
                <Avatar
                  src={PersonalizedImage}
                  alt='User Avatar'
                  sx={{
                    width: { xs: 64, lg: 78 },
                    height: { xs: 60, lg: 74 },
                    margin: '0 auto',
                    zIndex: 1,
                    marginTop: '40px'
                  }}
                />
                <Box
                  component='img'
                  src={AudioWave}
                  alt='Audio Visualizer'
                  position='absolute'
                  top='86px'
                  left='0'
                  sx={{ height: '80px', width: '100%' }}
                />
              </Box>
              <Typography variant='body2' sx={{ marginTop: '1rem' }}>
                Listening...
              </Typography>

              <Box
                sx={{
                  display: 'flex',
                  justifyContent: 'center',
                  gap: '1rem',
                  marginTop: '1.5rem'
                }}
              >
                <IconButton
                  sx={{
                    backgroundColor: theme.palette.primary.main,
                    height: { xs: 24, lg: 26 },
                    width: { xs: 24, lg: 26 },
                    color: '#fff',
                    '&:hover': {
                      backgroundColor: theme.palette.primary.dark
                    }
                  }}
                >
                  <MdMic />
                </IconButton>
                <IconButton
                  sx={{
                    backgroundColor: theme.palette.primary.main,
                    height: { xs: 24, lg: 26 },
                    width: { xs: 24, lg: 26 },
                    color: '#fff',
                    '&:hover': {
                      backgroundColor: theme.palette.primary.dark
                    }
                  }}
                >
                  <HiChatBubbleBottomCenterText />
                </IconButton>
              </Box>

              <Box
                sx={{
                  marginTop: '6rem',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: '0.5rem'
                }}
              >
                <img alt='logo' src={organizations?.logo_url} height={24} width={24} />
                <Typography variant='caption' fontSize={10}>
                  Powered by {organizations?.name}
                </Typography>
              </Box>
              <Box width={76} height={3} bgcolor='#000' borderRadius={50} mx='auto' mt={2} />
            </Box>
          </Box>
        </Grid>
        <Grid size={{ xs: 12, md: 8 }}>
          <Box
            bgcolor='#fff'
            padding={{ xs: 2, lg: 3 }}
            height='100%'
            mt={1}
            sx={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
              borderRadius: '8px'
            }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', width: '100%' }}>
              <PersonalizedLogo />
              <Typography variant='h6' fontSize='10px' ml={1} sx={{ fontSize: { xs: 10, lg: 10 } }}>
                Smile Mate Dental Clinic
              </Typography>
              <Box sx={{ marginLeft: 'auto' }}>
                <IconButton
                  sx={{
                    p: { xs: 0.5, lg: 1 }
                  }}
                >
                  <MdOutlineNotifications fontSize={14} />
                </IconButton>
              </Box>
            </Box>

            <Box
              sx={{
                width: '100%',
                maxWidth: '228px',
                padding: '2rem',
                backgroundColor: '#fff',
                boxShadow: '0 4px 12px rgba(0,0,0,0.1)',
                borderRadius: '12px',
                textAlign: 'center',
                marginY: '2rem'
              }}
            >
              <Box position='relative'>
                <Typography variant='body2' fontWeight={500} sx={{ marginTop: '1rem' }} marginBottom={0.5}>
                  John
                </Typography>
                <Typography variant='body2' fontSize='10px' fontWeight={500}>
                  01:23
                </Typography>
                <Avatar
                  src={PersonalizedImage}
                  alt='User Avatar'
                  sx={{
                    width: { xs: 64, lg: 78 },
                    height: { xs: 60, lg: 74 },
                    margin: '0 auto',
                    zIndex: 1,
                    marginTop: '40px'
                  }}
                />
                <Box
                  component='img'
                  src={AudioWave}
                  alt='Audio Visualizer'
                  position='absolute'
                  top='86px'
                  left='0'
                  sx={{ height: '80px', width: '100%' }}
                />
              </Box>
              <Typography variant='body2' sx={{ marginTop: '1rem' }}>
                Listening...
              </Typography>

              <Box
                sx={{
                  display: 'flex',
                  justifyContent: 'center',
                  gap: '1rem',
                  marginTop: '1.5rem'
                }}
              >
                <IconButton
                  sx={{
                    backgroundColor: theme.palette.primary.main,
                    height: { xs: 24, lg: 26 },
                    width: { xs: 24, lg: 26 },
                    color: '#fff',
                    '&:hover': {
                      backgroundColor: theme.palette.primary.dark
                    }
                  }}
                >
                  <MdMic />
                </IconButton>
                <IconButton
                  sx={{
                    backgroundColor: theme.palette.primary.main,
                    height: { xs: 24, lg: 26 },
                    width: { xs: 24, lg: 26 },
                    color: '#fff',
                    '&:hover': {
                      backgroundColor: theme.palette.primary.dark
                    }
                  }}
                >
                  <HiChatBubbleBottomCenterText />
                </IconButton>
              </Box>

              <Box
                sx={{
                  marginTop: '1rem',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: '0.5rem'
                }}
              >
                <img alt='logo' src={organizations?.logo_url} height={24} width={24} />
                <Typography variant='caption' fontSize={10}>
                  Powered by {organizations?.name}
                </Typography>
              </Box>
            </Box>
          </Box>
        </Grid>
      </Grid>
    </Box>
  );
}
